import {forwardRef} from "react";
import {Box, BoxProps, InputBase, Text, Badge} from "@mantine/core";
import {EventFormat, EventType, IEvent, TEventId, getEventTypeLabel} from "@/common/types";
import {IProtocolStepEvent, TProtocolStepEventId} from "@/protocols/types/IProtocolStepEvent";
import classes from "./EventListView.module.css";
import {EventDetailInput} from "@/common/components/EventDetailInput.tsx";
import {PPReliefEventDetail} from "@/common/components/PPReliefEventDetail.tsx";
import {IconTrash, IconAlertTriangle} from "@tabler/icons-react";
import dayjs from "dayjs";
import {ZoneAwareDateTimePicker} from "@/common/components/ZoneAwareDateTimePicker.tsx";
import cx from "clsx";

interface EventListViewProps extends BoxProps {
    /** Events to display - can be IEvent[] (from recording) or IProtocolStepEvent[] (from review) */
    events: Array<IProtocolStepEvent> | Array<IEvent>;
    /** Map of event ID to warning message for events outside step boundaries */
    eventWarnings?: Map<TProtocolStepEventId, string>;
    /** Timezone for displaying timestamps */
    timezone: string;
    /** Callback when delete is clicked */
    onDeleteEvent?: (id: TEventId) => void;
    /** If true, events are displayed read-only. Defaults to false. */
    readonly?: boolean;
    /** If true, the delete button is shown. Defaults to `!readonly`. Set explicitly to override. */
    canDelete?: boolean;
}

/**
 * Unified EventListView component that handles both recording context (IEvent[])
 * and review context (IProtocolStepEvent[] with optional warnings).
 *
 * Normalizes to IEvent[] internally at entry point for clean processing.
 */
export const EventListView = forwardRef<HTMLDivElement, EventListViewProps>(({
                                                                                 events,
                                                                                 eventWarnings,
                                                                                 timezone,
                                                                                 className,
                                                                                 onDeleteEvent = () => {
                                                                                 },
                                                                                 readonly = false,
                                                                                 canDelete,
                                                                                 ...others
                                                                             }, ref) => {
    const showDelete = canDelete ?? !readonly;
    // Normalize to IEvent[] - accepts both IEvent[] and IProtocolStepEvent[]
    const normalizedEvents: IEvent[] = events?.map(e => 'event' in e ? e.event : e) ?? [];

    // Sort events by timestamp
    const sortedEvents = [...normalizedEvents].sort((a, b) => {
        return dayjs(b.startTime).valueOf() - dayjs(a.startTime).valueOf();
    });

    const renderEventRow = (event: IEvent) => {
        const eventId = event.id;
        const warning = eventWarnings?.get(eventId as TProtocolStepEventId);
        const format = "format" in event.metadata ? event.metadata["format"] as EventFormat : EventFormat.TEXT;

        const renderDetails = () => {
            if (event.eventType === EventType.PP_RELIEF) {
                const score = event.metadata["score"] as number | undefined;
                return <PPReliefEventDetail score={score} readonly={true}/>;
            }

            let details = "";
            if (event.eventType === EventType.NOTE) {
                details = event.metadata["detail"];
            } else if (event.eventType === EventType.IMPEDANCE_MEASURE) {
                details = (event.metadata["impedances"] as [{
                    channel: string,
                    magnitude: number,
                    unit: string
                }])?.reduce((acc, imp) => {
                    acc += `${imp.channel}: ${imp.magnitude}${imp.unit}, `
                    return acc
                }, "")
            }
            return (
                <EventDetailInput
                    format={format}
                    details={details}
                    readonly={readonly}
                    onDetailChange={() => {}}
                    isPlaying={false}
                    showLabel={false}
                />
            );
        };

        return (
            <Box
                key={eventId}
                className={classes.eventRow}
                style={warning ? {borderLeft: "3px solid var(--mantine-color-yellow-6)"} : undefined}
            >
                <Box style={{display: "flex", flexDirection: "column", gap: "0.25rem"}}>
                    <ZoneAwareDateTimePicker
                        timezone={timezone}
                        value={dayjs(event.startTime)}
                        onChange={() => {}}
                        label=""
                        readonly={readonly}
                    />
                    {warning && (
                        <Badge
                            size="xs"
                            color="yellow"
                            leftSection={<IconAlertTriangle size={10}/>}
                            title={warning}
                        >
                            Out of bounds
                        </Badge>
                    )}
                </Box>
                <InputBase value={getEventTypeLabel(event.eventType as EventType)} disabled={readonly}/>
                <Box className={classes.detailCell}>
                    {renderDetails()}
                </Box>
                <Box className={classes.actions}>
                    {showDelete && <IconTrash
                        color="red"
                        className={cx(classes.deleteEvent)}
                        size={"1rem"}
                        data-testid={`delete-event-${eventId}`}
                        onClick={() => onDeleteEvent(eventId as TEventId)}
                    />
                    }
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Box className={classes.eventRow}>
                <Text size="xs" fw="bold">Start Time</Text>
                <Text size="xs" fw="bold">Type</Text>
                <Text size="xs" fw="bold">Details</Text>
            </Box>
            <Box ref={ref} className={`${classes.eventsList} ${className || ""}`} {...others}>
                {sortedEvents.length > 0 ? sortedEvents.map(event => renderEventRow(event)) : (
                    <Box className={classes.eventRow}>
                        <Text style={{gridColumn: "1 / -1"}} color="dimmed" size="sm">No events recorded. </Text>
                    </Box>
                )}
            </Box>
        </>
    );
});